import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SparkleIcon } from '@/components/icons/SparkleIcon.tsx';
import { useProjectStore } from '@/store/projectStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { capabilities } from '@/capabilities.ts';
import styles from './AiGutterButton.module.css';

export function AiGutterButton() {
  const { t } = useTranslation('aiWorkspace');
  const project = useProjectStore(s => s.project);
  const readOnly = useProjectStore(s => s.readOnly);
  const aiWorkspaceOpen = useUiStore(s => s.aiWorkspaceOpen);
  const openAiWorkspace = useUiStore(s => s.openAiWorkspace);
  const closeAiWorkspace = useUiStore(s => s.closeAiWorkspace);
  const glowTutorialStep = useUiStore(s => s.glowTutorialStep);
  const setGlowTutorialStep = useUiStore(s => s.setGlowTutorialStep);
  const isAnyGenerating = useAiStore(s => s.isAnyGenerating);

  const [introGlow, setIntroGlow] = useState(true);

  // Auto-clear intro glow after 8 seconds
  useEffect(() => {
    if (!introGlow) return;
    const timer = setTimeout(() => setIntroGlow(false), 8000);
    return () => clearTimeout(timer);
  }, [introGlow]);

  const setTeaseOverlay = useUiStore(s => s.setTeaseOverlay);
  const disabled = !readOnly && (!capabilities.aiGeneration || !project);
  const isTutorialGlow = glowTutorialStep === 'gutter';

  const handleClick = () => {
    if (readOnly) { setTeaseOverlay('ai'); return; }
    setIntroGlow(false);
    if (isTutorialGlow) {
      // The workspace lands on the Create tab by default since 2026-08-25, but the glow hand-off
      // below points at StyleGradeTab's data-glow-target="enhance" — force that tab or the tour's
      // next beat has nothing to light up.
      // Written BEFORE the open (T1+T2, 2026-09-01): the flow's open effect reads aiWorkspaceTab
      // to decide whether to seed the playhead shot, so the tab must land first — which is also
      // what keeps this tour branch on the seeded Enhance path.
      useUiStore.getState().setAiWorkspaceTab('stylegrade');
      openAiWorkspace('enhance');
      setGlowTutorialStep('enhance');
    } else {
      if (aiWorkspaceOpen === true) {
        closeAiWorkspace();
      } else {
        // 20260924 — owner: opening AI Studio straight from the Timeline lands on Enhance, not the
        // Create landing every other unscoped caller keeps. Written BEFORE the open for the same
        // reason as the tour branch above, so this open takes the flow's Enhance (playhead) seed.
        useUiStore.getState().setAiWorkspaceTab('stylegrade');
        openAiWorkspace('enhance');
      }
    }
  };

  return (
    <div className={styles.wrapper}>
      <button
        className={`${styles.btn} ${aiWorkspaceOpen ? styles.btnActive : ''} ${isAnyGenerating() ? styles.btnGenerating : ''} ${introGlow && !aiWorkspaceOpen && !isAnyGenerating() ? styles.btnIntro : ''} ${isTutorialGlow ? styles.tutorialGlow : ''}`}
        onClick={handleClick}
        disabled={disabled}
        title={t('aiWorkspaceTooltip')}
        data-glow-target="gutter"
      >
        <SparkleIcon />
        <span className={styles.label}>{t('gutterLabel')}</span>
      </button>
    </div>
  );
}
